import { ref, shallowRef, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue';
import * as widgetSdk from '@mxenabled/web-widget-sdk';
import { useAppStore } from '@/store/app.js';
import { handleError } from '@/utils/error-handler';

/**
 * Composable for MX widget management, account sync, and event tracking.
 *
 * @param {import('vue').Ref<Object>} wallet
 * @param {import('vue').Ref<string>} institutionCode
 * @param {import('vue').Ref<string>} institutionName
 * @param {import('vue').Ref<number>} connectStep
 * @param {() => string} getConnectType - getter for connectType prop
 * @param {() => Object|null} getMember - getter for member prop
 * @param {Function} emit
 * @param {Function} notify - $notify function
 */
export function useBankConnection(
    wallet,
    institutionCode,
    institutionName,
    connectStep,
    getConnectType,
    getMember,
    emit,
    notify,
    onAddCardSuccess = null
) {
    const widgetUrl = ref('');
    const mxWidget = shallowRef(null);
    const loadingConnectWidget = ref(false);
    const completedConnect = ref(false);
    const finshedSyncAccount = ref(false);
    const showAccountSyncDelayMessage = ref(false);
    const connectedAccounts = ref([]);
    const showWaitForAccountsOverlay = ref(false);
    const addCardBtnAction = ref(false);
    const showInstitutionsDropdown = ref(false);
    const errors = ref([]);

    function unmountWidget() {
        if (mxWidget.value) {
            try {
                mxWidget.value.unmount();
            } catch (error) {
                console.warn('MX Widget cleanup warning:', error);
            } finally {
                mxWidget.value = null;
            }
        }
    }

    function clearWidgetContainer(containerEl) {
        nextTick(() => {
            if (containerEl && containerEl.parentNode) {
                containerEl.innerHTML = '';
            }
        });
    }

    function connectLog(type, payload) {
        ApiService.apiCall(
            '/wallets/' + wallet.value.id + '/connect-log',
            'post',
            {
                status: type,
                metadata: payload,
            }
        );
    }

    function trackHubSpotEvent(eventName, extraProperties) {
        if (!window._hsq) return;

        const appStore = useAppStore();
        const loggedInUser = appStore.userSettings;
        if (!loggedInUser) return;

        let userFirstName = null;
        let userLastName = null;

        if (loggedInUser.name) {
            const nameParts = loggedInUser.name.split(' ');
            userFirstName = nameParts[0];
            if (nameParts.length > 1) {
                userLastName = nameParts.slice(1).join(' ');
            }
        }

        const eventProperties = {
            email: loggedInUser.email || null,
            firstname: userFirstName,
            lastname: userLastName,
            my10x_user_uuid: loggedInUser.uuid || null,
            institution_id: institutionCode.value,
            institution_name: institutionName.value,
            ...extraProperties,
        };

        // Remove null/undefined properties
        for (const key in eventProperties) {
            if (eventProperties[key] === null || eventProperties[key] === undefined) {
                delete eventProperties[key];
            }
        }

        window._hsq.push([
            'trackCustomBehavioralEvent',
            { name: eventName, properties: eventProperties },
        ]);
    }

    function trackWoopra() {
        try {
            if (import.meta.env.VITE_APP_ENV === 'production') {
                woopra.track('bank-connected-my10x', {
                    datetime: new Date(),
                    bankName: institutionCode.value,
                    uuid: localStorage.getItem('uuid') || 'unknown',
                });
            }
        } catch (e) {
            console.error('Woopra tracking error:', e);
        }
    }

    function presentConnectIframe(getContainer, accountUserForCard, gotoStepFn, closeFn) {
        nextTick(() => {
            const container = getContainer();
            if (!container) {
                console.error('connect-widget container not found in DOM!');
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to initialize connection widget. Please try again.',
                    },
                    4000
                );
                return;
            }

            if (!widgetUrl.value || !widgetUrl.value.url) {
                console.error('Widget URL is invalid:', widgetUrl.value);
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to initialize connection widget. Invalid URL.',
                    },
                    4000
                );
                return;
            }

            try {
                unmountWidget();

                // Double-check container is mounted
                const widgetContainer = getContainer();
                if (!widgetContainer || !widgetContainer.parentNode) {
                    console.error('MX Widget container element not found or not properly mounted');
                    loadingConnectWidget.value = false;
                    return;
                }

                mxWidget.value = new widgetSdk.ConnectWidget({
                    container: '#connect-widget',
                    url: widgetUrl.value.url,
                    iframeTitle: 'Connect',
                    style: {
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    },
                    onLoaded: (payload) => {
                        loadingConnectWidget.value = false;
                        connectLog('loaded', payload);
                    },
                    onBackToSearch: (payload) => {
                        loadingConnectWidget.value = true;

                        const widgetEl = getContainer();
                        if (widgetEl) {
                            unmountWidget();
                            clearWidgetContainer(widgetEl);
                        }

                        connectLog('backToSearch', payload);
                        gotoStepFn(3);
                    },
                    onMemberConnected: (payload) => {
                        connectLog('memberConnected', payload);

                        if (getConnectType() === 'memberRequest') {
                            resyncConnectedAccounts(payload);

                            notify(
                                {
                                    group: 'success',
                                    title: 'Account connected',
                                    text: 'Your institution connection has been reconnected. We will attempt to download your accounts again shortly.',
                                },
                                5000
                            );

                            closeFn();
                            emit('addedCard');
                        } else {
                            syncConnectedAccounts(payload, accountUserForCard);
                        }

                        // HubSpot Event Tracking for Connect Bank to Wallet
                        trackHubSpotEvent(
                            'pe22692776_finished_connecting_account_to_wallet',
                            {
                                connection_status: 'success',
                                public_token_received: true,
                            }
                        );

                        // Safely unmount the MX widget
                        unmountWidget();

                        trackWoopra();
                    },
                    onCreateMemberError: (payload) => {
                        console.error('MX Widget create member error:', payload);
                        connectLog('createMemberError', payload);
                        notify(
                            {
                                group: 'error',
                                title: 'Connection Error',
                                text: 'There was an error connecting to your bank. Please try again or contact support.',
                            },
                            10000
                        );
                    },
                    onPrimaryAction: (payload) => {
                        connectLog('primaryAction', payload);
                    },
                    onEnterCredentials: (payload) => {
                        connectLog('enterCredentials', payload);
                    },
                    onMemberStatusUpdate: (payload) => {
                        connectLog('memberStatusUpdate', payload);
                    },
                    onOauthError: (payload) => {
                        console.error('MX Widget oauth error:', payload);
                        connectLog('oauthError', payload);
                    },
                    onOauthRequested: (payload) => {
                        connectLog('oauthRequested', payload);
                    },
                    onStepChange: (payload) => {
                        connectLog('stepChange', payload);
                    },
                    onSubmitMFA: (payload) => {
                        connectLog('submitMFA', payload);
                    },
                    onUpdateCredentials: (payload) => {
                        connectLog('updateCredentials', payload);
                    },
                });
            } catch (e) {
                console.error('Error initializing MX widget:', e);
                loadingConnectWidget.value = false;
            }
        });
    }

    function getWidgetUrl(getContainer, accountUserForCard, gotoStepFn, closeFn, externalErrors = null) {
        const errorsTarget = externalErrors ?? errors;
        // HubSpot Event Tracking for Connect Bank to Wallet (Clicked "Connect to Bank")
        trackHubSpotEvent('pe22692776_connect_bank_to_wallet', {});

        loadingConnectWidget.value = true;
        errorsTarget.value = [];

        // Skip institution code check for member requests
        if (getConnectType() !== 'memberRequest' && institutionCode.value === '') {
            errorsTarget.value.push({
                message: 'Please choose your financial institution.',
            });
            loadingConnectWidget.value = false;
            return;
        }

        showInstitutionsDropdown.value = false;
        loadingConnectWidget.value = true;
        addCardBtnAction.value = true;
        let data = { institution_code: institutionCode.value };

        // For member requests (fixing connections), add the required parameters
        if (getConnectType() === 'memberRequest') {
            const member = getMember();
            if (!member || !member.guid) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to reconnect: Member information is missing.',
                    },
                    4000
                );
                addCardBtnAction.value = false;
                loadingConnectWidget.value = false;
                return;
            }

            data = {
                is_member_request: 1,
                member_guid: member.guid,
            };
        }

        ApiService.apiCall(
            '/wallets/' + wallet.value.id + '/url',
            'post',
            data
        )
            .then((response) => {
                if (typeof response.data.error !== 'undefined') {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'Bank connect error: ' + response.data.error.message,
                        },
                        4000
                    );
                    loadingConnectWidget.value = false;
                    showInstitutionsDropdown.value = true;
                } else {
                    widgetUrl.value = response.data.widget_url;
                    presentConnectIframe(getContainer, accountUserForCard, gotoStepFn, closeFn);
                }
                addCardBtnAction.value = false;
            })
            .catch((error) => {
                handleError(error);
                addCardBtnAction.value = false;
                loadingConnectWidget.value = false;
                showInstitutionsDropdown.value = true;
            });
    }

    function syncConnectedAccounts(response, accountUserForCard) {
        if (!completedConnect.value) {
            const data = {
                user: accountUserForCard,
                user_guid: response.user_guid,
                member_guid: response.member_guid,
            };

            showWaitForAccountsOverlay.value = true;

            ApiService.apiCall(
                '/wallets/' + wallet.value.id + '/accounts-sync/',
                'post',
                data
            )
                .then((response) => {
                    const guid = response.data.guid;
                    completedConnect.value = true;
                    startAccountSyncTimer(guid);
                })
                .catch((response) => {
                    handleError(response);
                });
        }
    }

    function resyncConnectedAccounts(payload) {
        const data = {
            user_guid: payload.user_guid,
            member_guid: payload.member_guid,
        };

        ApiService.apiCall(
            '/wallets/' + wallet.value.id + '/accounts-resync/',
            'post',
            data
        )
            .then(() => {})
            .catch((error) => {
                handleError(error);
            });
    }

    const syncTimerRef = ref(null);

    function startAccountSyncTimer(guid) {
        if (syncTimerRef.value) {
            clearTimeout(syncTimerRef.value);
        }
        syncTimerRef.value = setTimeout(() => {
            syncTimerRef.value = null;
            syncNewAccounts(guid);
        }, 65000);
    }

    function cancelAccountSyncTimer() {
        if (syncTimerRef.value) {
            clearTimeout(syncTimerRef.value);
            syncTimerRef.value = null;
        }
    }

    function syncNewAccounts(guid) {
        ApiService.apiCall(
            '/wallets/' + wallet.value.id + '/new-accounts-sync/' + guid,
            'get'
        )
            .then((response) => {
                finshedSyncAccount.value = true;

                let rewardsMessage = response.data.has_rewards
                    ? 'Your rewards will sync automatically each night.'
                    : 'You will need to manually enter your rewards balance.';

                notify(
                    {
                        group: 'success',
                        title: 'Success',
                        text: 'Your rewards program has been created. ' + rewardsMessage,
                    },
                    10000
                );

                if (response.data.accounts.length > 0) {
                    if (typeof onAddCardSuccess === 'function') {
                        onAddCardSuccess();
                    } else {
                        emit('addedCard');
                    }
                } else {
                    showAccountSyncDelayMessage.value = true;
                }
            })
            .catch((response) => {
                finshedSyncAccount.value = true;
                handleError(response);
            });
    }

    function resetState(getContainer) {
        cancelAccountSyncTimer();
        unmountWidget();

        nextTick(() => {
            const widgetContainer = getContainer();
            if (widgetContainer && widgetContainer.parentNode) {
                widgetContainer.innerHTML = '';
            }
        });

        loadingConnectWidget.value = false;
        completedConnect.value = false;
        finshedSyncAccount.value = false;
        showAccountSyncDelayMessage.value = false;
        showWaitForAccountsOverlay.value = false;
        showInstitutionsDropdown.value = false;
        widgetUrl.value = '';
    }

    if (getCurrentInstance()) {
        onBeforeUnmount(() => {
            cancelAccountSyncTimer();
            unmountWidget();
        });
    }

    return {
        widgetUrl,
        mxWidget,
        loadingConnectWidget,
        completedConnect,
        finshedSyncAccount,
        showAccountSyncDelayMessage,
        connectedAccounts,
        showWaitForAccountsOverlay,
        addCardBtnAction,
        showInstitutionsDropdown,
        errors: errors,
        unmountWidget,
        clearWidgetContainer,
        connectLog,
        getWidgetUrl,
        presentConnectIframe,
        syncConnectedAccounts,
        resyncConnectedAccounts,
        resetState,
        cancelAccountSyncTimer,
    };
}
